import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Search, UserCheck, X } from 'lucide-react'
import { Button } from '@/components/ui'
import {
  fetchPartnerStatusCandidates,
  fetchPartnerStatusContext,
  setPartnerStatus,
  type PartnerStatusCandidate,
  type PartnerStatusHistoryRow,
  type PartnerStatusMode,
} from '@/services/partnerStatusService'

export type { PartnerStatusMode }

const MODE_LABELS: Record<PartnerStatusMode, string> = {
  termination: 'Франчайзи на расторжении',
  former: 'Франчайзи бывший',
}

const TARGET_STATUS_LABELS: Record<PartnerStatusMode, string> = {
  termination: 'на расторжении',
  former: 'бывший',
}

const DEFAULT_TIME: Record<PartnerStatusMode, string> = {
  termination: '12:00',
  former: '15:00',
}

const INPUT_CLASS =
  'mt-1 w-full rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary'

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

function matchesQuery(haystack: Array<string | number | null | undefined>, query: string): boolean {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) return true
  return haystack
    .filter(value => value !== null && value !== undefined && value !== '')
    .join(' ')
    .toLowerCase()
    .includes(trimmed)
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value.includes('T') ? value : value.replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function timestampFromInputs(date: string, time: string): string | null {
  if (!date || !time) return null
  const normalizedTime = time.length === 5 ? `${time}:00` : time
  return `${date} ${normalizedTime}`
}

function partnerLabel(partner: PartnerStatusCandidate): string {
  const fio = partner.persons_fio.trim() || 'без персон'
  return `№${partner.partner_number} · ${fio}`
}

export function PartnerStatusModal({
  initialMode,
  onBack,
  onClose,
  onDone,
}: {
  initialMode: PartnerStatusMode
  onBack?: () => void
  onClose: () => void
  onDone?: () => void
}) {
  const mode = initialMode
  const [partners, setPartners] = useState<PartnerStatusCandidate[]>([])
  const [historyRows, setHistoryRows] = useState<PartnerStatusHistoryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [partnerQuery, setPartnerQuery] = useState('')
  const [partnerNumber, setPartnerNumber] = useState<number | null>(null)
  const [changedDate, setChangedDate] = useState(todayIso())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  async function loadPartners() {
    setLoading(true)
    try {
      setPartners(await fetchPartnerStatusCandidates())
      setError(null)
    } catch (loadError: unknown) {
      setError(errorMessage(loadError, 'Не удалось загрузить франчайзи'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadPartners()
  }, [])

  const filteredPartners = useMemo(() => {
    return partners
      .filter(row => matchesQuery([
        row.partner_number,
        row.persons_fio,
        row.current_status,
        row.cities_text,
      ], partnerQuery))
      .slice(0, 80)
  }, [partnerQuery, partners])

  const selectedPartner = useMemo(
    () => partners.find(row => row.partner_number === partnerNumber) ?? null,
    [partnerNumber, partners]
  )

  useEffect(() => {
    if (loading) return
    if (partnerNumber !== null && filteredPartners.some(row => row.partner_number === partnerNumber)) return
    setPartnerNumber(filteredPartners.length === 1 ? filteredPartners[0].partner_number : null)
  }, [filteredPartners, loading, partnerNumber])

  useEffect(() => {
    let cancelled = false

    if (partnerNumber === null) {
      setHistoryRows([])
      return
    }

    setHistoryLoading(true)
    fetchPartnerStatusContext(partnerNumber)
      .then(rows => {
        if (!cancelled) setHistoryRows(rows)
      })
      .catch(loadError => {
        if (!cancelled) setError(errorMessage(loadError, 'Не удалось загрузить историю статуса'))
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [partnerNumber])

  const changedAt = timestampFromInputs(changedDate, DEFAULT_TIME[mode])
  const canSubmit = !saving && !loading && partnerNumber !== null && Boolean(changedAt)
  const latestStatus = historyRows.find(row => row.section === 'Текущий статус')
  const historyOnly = historyRows.filter(row => row.section === 'История')

  async function handleSubmit() {
    if (!canSubmit || partnerNumber === null || !changedAt) return

    const confirmed = window.confirm(
      `Перевести франчайзи №${partnerNumber} в статус «${TARGET_STATUS_LABELS[mode]}»?\n\n`
      + `Дата и время: ${formatDateTime(changedAt)}.`
    )
    if (!confirmed) return

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const result = await setPartnerStatus({
        mode,
        targetPartnerNumbers: [partnerNumber],
        changedAt,
      })
      setSuccess(
        result.updated_count > 0
          ? `Франчайзи №${partnerNumber} переведен в статус «${result.target_partner_status}».`
          : `Франчайзи №${partnerNumber} уже был в статусе «${result.target_partner_status}», новая строка не добавлена.`
      )
      await loadPartners()
      setHistoryRows(await fetchPartnerStatusContext(partnerNumber))
      onDone?.()
    } catch (submitError: unknown) {
      setError(errorMessage(submitError, 'Статус франчайзи не изменен'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-8" onClick={onClose}>
      <section
        className="my-6 w-full max-w-3xl rounded-xl bg-background p-4 shadow-lg"
        onClick={event => event.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                title="Вернуться к выбору кейса"
                aria-label="Вернуться к выбору кейса"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <UserCheck size={15} />
                {MODE_LABELS[mode]}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                Выберите франчайзи и точное время изменения статуса.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Закрыть"
          >
            <X size={16} />
          </button>
        </div>

        {error && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {success && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
            <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
            <div>{success}</div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="min-w-0">
            <label className="block text-xs font-medium text-muted-foreground">
              Франчайзи
              <div className="mt-1 flex h-9 items-center gap-2 rounded-md border border-border bg-background px-2 focus-within:border-primary">
                <Search size={15} className="text-muted-foreground" />
                <input
                  value={partnerQuery}
                  onChange={event => setPartnerQuery(event.target.value)}
                  className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                  placeholder="номер, ФИО, город, статус"
                />
              </div>
            </label>

            {loading ? (
              <div className="mt-1 flex h-[154px] items-center justify-center gap-2 rounded border border-border bg-muted/30 text-sm text-muted-foreground">
                <Loader2 size={16} className="animate-spin" />
                Загрузка
              </div>
            ) : (
              <select
                value={partnerNumber ?? ''}
                onChange={event => {
                  const value = event.target.value
                  setPartnerNumber(value ? Number(value) : null)
                  setSuccess(null)
                  setError(null)
                }}
                size={6}
                className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:border-primary"
              >
                {filteredPartners.map(partner => (
                  <option key={partner.partner_number} value={partner.partner_number}>
                    {partnerLabel(partner)}
                    {partner.current_status ? ` · ${partner.current_status}` : ''}
                    {partner.active_studio_count ? ` · студий ${partner.active_studio_count}` : ''}
                    {partner.cities_text ? ` · ${partner.cities_text}` : ''}
                  </option>
                ))}
              </select>
            )}
            <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
              Показано {filteredPartners.length} из {partners.length}
            </span>
          </div>

          <div className="min-w-0 space-y-3">
            {selectedPartner && (
              <div className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                <div className="text-sm font-semibold text-foreground">{partnerLabel(selectedPartner)}</div>
                <div className="mt-1">Сейчас: {latestStatus?.status ?? selectedPartner.current_status ?? '—'}</div>
                <div className="mt-1">Изменен: {formatDateTime(latestStatus?.changed_at ?? selectedPartner.status_changed_at)}</div>
                <div className="mt-1">Активные студии: {selectedPartner.active_studio_count}</div>
                {selectedPartner.cities_text && <div className="mt-1">Города: {selectedPartner.cities_text}</div>}
              </div>
            )}

            <label className="block text-xs font-medium text-muted-foreground">
              Дата изменения
              <input
                type="date"
                value={changedDate}
                onChange={event => setChangedDate(event.target.value)}
                className={INPUT_CLASS}
              />
            </label>

            <div className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
              <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
                История статусов
                {historyLoading && <Loader2 size={13} className="animate-spin text-muted-foreground" />}
              </div>
              {historyOnly.length === 0 ? (
                <div>Истории пока нет.</div>
              ) : (
                <div className="max-h-40 space-y-1 overflow-y-auto pr-1">
                  {historyOnly.map((row, index) => (
                    <div key={`${row.partner_number}-${row.changed_at}-${index}`} className="flex justify-between gap-3">
                      <span className="min-w-0 truncate">
                        {row.old_status ? `${row.old_status} -> ` : ''}
                        {row.status ?? '—'}
                      </span>
                      <span className="shrink-0 text-right">{formatDateTime(row.changed_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                Закрыть
              </Button>
              <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
                {saving && <Loader2 size={15} className="mr-2 animate-spin" />}
                Провести
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
